import { Component, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { S3UploadService, UploadedImage } from '../../services/uploadImages';
import { environment } from '../../../enviroment/enviroment';
import { MapaDireccionComponent } from '../../pages/map/map.component';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { map, Observable } from 'rxjs';
import { MatProgressSpinnerModule, MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { SuccessDialogComponent } from '../hosting-popup/success-dialog.component'; // 👈 importa esto
import { HostingService } from '../../services/hosting.service';


export interface HostingModalData {
  hosting?: any;
  mode: 'create' | 'edit';
}

// ===== Interfaces =====
interface BasicInfo {
  id?: string;
  status: boolean;
  name: string;
  email: string;
  phone: string;
  type: string;
  capacity: number | string;
  description: string;
  user_id: string;
}

interface LocationData {
  id?: string;
  full_address: string;
  coordinates: string;
  latitude: string;
  longitude: string;
  city: string;
  country: string;
  region: string;
}

interface Space {
  id: string;
  number: number;
  type: string;
  floor: number;
  beds: number;
  bathspaces: number;
  capacity: number; // 👈 NUEVO
  price_per_night: number;
  description: string;
  images: string[];
}



interface ExtraService {
  id: string;
  name: string;
  cost: number;
}

interface IncludedService {
  name: string;
  icon?: string;
}

interface Rule {
  id: string;
  title: string;
  category: string;
  description: string;
  penalty: string;
}

@Component({
  selector: 'app-hosting-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatStepperModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressBarModule,
    MatChipsModule,
    MapaDireccionComponent,
    MatProgressSpinner
],
  templateUrl: './hosting-modal.component.html',
  styleUrls: ['./hosting-modal.component.css']
})

export class HostingModalComponent implements OnInit {

  @ViewChild(MapaDireccionComponent) mapComponent!: MapaDireccionComponent;

  // ===== States =====
  isEditMode = false;
  currentStep = 0;
  isLoading = false;
  loadingMessage = 'Procesando...';

  // ===== Step 1: Basic Info =====
  basicInfo: BasicInfo = {
    status: true, name: '', email: '', phone: '', type: '', capacity: '', description: '', user_id: ''
  };

  // ===== Step 2: Location =====
  selectedCoords = { lat: 4.7110, lng: -74.0721 };
  locationData: LocationData = {
    full_address: '', coordinates: '', latitude: '', longitude: '',
    city: '', country: '', region: ''
  };

  // ===== Step 3: Images =====
  hostingImages: UploadedImage[] = [];
  uploadProgress = { visible: false, percentage: 0, uploaded: 0, total: 0 };
  isDragOver = false;

  // ===== Step 4: Spaces =====
  spaces: Space[] = [];
  newSpace: Partial<Space> = {};
  spaceImages: string[] = [];
  spaceImagesPreview: string[] = [];
  spaceImagesLoading: boolean = false;
  spaceImagesError: string = '';

  // ===== Step 5: Services & Payments =====
  extraServices: ExtraService[] = [];
  includedServices: IncludedService[] = [];
  paymentMethods: any[] = [];
  hostingTypes: string[] = [
    'Cabaña', 'Resort', 'Eco Lodge', 'Hotel'
  ];

  serviceExtraTemp: ExtraService = { id: '', name: '', cost: 0 };
  serviceIncludedTemp: string = '';
  paymentMethodTemp: string = '';

  avaibleServiceIncluded = [
    { name: 'Wi-Fi', icon: 'wifi' },
    { name: 'Aire acondicionado', icon: 'ac_unit' },
    { name: 'Televisión', icon: 'tv' },
    { name: 'Desayuno', icon: 'breakfast_dining' },
    { name: 'Gimnasio', icon: 'fitness_center' },
    { name: 'Piscina', icon: 'pool' },
    { name: 'Spa', icon: 'spa' },
    { name: 'Zona de juegos infantil', icon: 'child_care' },
    { name: 'Pet friendly', icon: 'pets' } // o "Apto para mascotas"
  ];

  availablePaymentMethods = [
    { name: 'Efectivo' },
    { name: 'Tarjeta de crédito / débito' },
    { name: 'PSE' },
    { name: 'PayPal' },
    { name: 'Nequi' },
    { name: 'Efecty' },
    { name: 'Bancolombia' },
    { name: 'Daviplata' },
    { name: 'Transferencia bancaria' }
  ];


  // ===== Optional: Rules =====
  rule: Rule = {
    id: '',
    title: '',
    category: '',
    description: '',
    penalty: ''
  };
  newRule: Partial<Rule> = {};
  stepperOrientation!: Observable<'horizontal' | 'vertical'>;


  constructor(
    private dialogRef: MatDialogRef<HostingModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HostingModalData,
    private s3UploadService: S3UploadService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private hostingService: HostingService
  ) {
    this.isEditMode = data.mode === 'edit';
    this.stepperOrientation = this.breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(
        map((state: BreakpointState) =>
          state.matches ? 'horizontal' : 'vertical'
        )
      );
  }

  ngOnInit(): void {
    if (this.isEditMode && this.data.hosting) {
      this.populateForm(this.data.hosting);
    }
  }

  // ====================== FORM HANDLING ======================
  private populateForm(hosting: any): void {
    this.basicInfo = {
      id: hosting.id,
      status: hosting.status,
      name: hosting.name,
      email: hosting.email,
      phone: hosting.phone,
      description: hosting.description,
      capacity: hosting.capacity,
      type: hosting.type,
      user_id: hosting.user_id || ''
    };

    this.locationData = {
      id: hosting.location?.id || '',
      full_address: hosting.location?.full_address || '',
      coordinates: `${hosting.location?.latitude}, ${hosting.location?.longitude}`,
      latitude: hosting.location?.latitude || '',
      longitude: hosting.location?.longitude || '',
      city: hosting.location?.city || '',
      country: hosting.location?.country || '',
      region: hosting.location?.region || ''
    };

    this.paymentMethods = hosting.payment_methods || [];
    this.extraServices = hosting.extra_services || [];
    this.includedServices = hosting.included_services || [];
    this.spaces = hosting.spaces || [];
    this.rule = hosting.rule || {};
    this.hostingImages = (hosting.images || []).map((url: string) => ({
      url, file: new File([], ''), uploaded: true, key: '', uploading: false, preview: url
    }));
  }

  // ====================== STEP NAVIGATION ======================
  nextStep(): void {
  if (this.validateCurrentStep()) {
    this.currentStep++;
    this.scrollModalToTop();
  }
}


  previousStep(): void {
    if (this.currentStep > 0) this.currentStep--;
  }

  validateCurrentStep(): boolean {
    switch (this.currentStep) {
      case 0: return this.validateBasicInfo();
      case 1: return this.validateLocation();
      case 2: return this.validateImages();
      case 3: return this.validateSpaces();
      case 4: return this.validatePaymentMethods();
      default: return true;
    }
  }

  // ====================== VALIDATIONS ======================
  validateBasicInfo(): boolean {
    const { name, email, phone, type } = this.basicInfo;
    return !!(name && email && phone && type);
  }

  validateLocation(): boolean {
    const { city, country, latitude, longitude } = this.locationData;
    return !!(city && country && latitude && longitude);
  }

  validateImages(): boolean {
    return this.uploadedImages.length > 0;
  }

  validateSpaces(): boolean {
  return this.spaces.length > 0 && this.spaces.every(space =>
    space.number > 0 &&
    !!space.type &&
    space.capacity > 0 &&
    space.price_per_night > 0 &&
    space.images.length > 0
  );
}


  validatePaymentMethods(): boolean {
    return this.paymentMethods.length > 0;
  }

  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      await this.processImages(files);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  // ====================== IMAGES ======================
  get uploadedImages(): UploadedImage[] {
    return this.hostingImages.filter(img => img.uploaded);
  }

  async handleFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      await this.processImages(Array.from(input.files));
      input.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async handleFilesDropped(event: DragEvent): Promise<void> {
    event.preventDefault();
    if (!event.dataTransfer?.files) return;
    const files = Array.from(event.dataTransfer.files)
      .filter(f => f.type.startsWith('image/'));
    if (files.length) await this.processImages(files);
  }

  private async processImages(files: File[]): Promise<void> {
    const MAX_IMAGES = 10;
    if (this.hostingImages.length + files.length > MAX_IMAGES) {
      alert(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    this.uploadProgress = { visible: true, percentage: 0, uploaded: 0, total: files.length };

    try {
      const hostingId = this.generateHostingId();
      const uploaded = await this.s3UploadService.uploadMultipleImages(
        files, hostingId, (progress, uploadedCount) => {
          this.uploadProgress.percentage = Math.round(progress);
          this.uploadProgress.uploaded = uploadedCount;
        }
      );

      this.hostingImages.push(...uploaded);

    } catch (err) {
      console.error('Image upload error:', err);
      alert('Error uploading images');
    } finally {
      setTimeout(() => this.uploadProgress.visible = false, 1500);
    }
  }

  markAsMain(index: number): void {
    if (index === 0) return;
    const updated = [...this.hostingImages];
    const selected = updated.splice(index, 1)[0];
    updated.unshift(selected);
    this.hostingImages = updated;
  }

  async deleteImage(index: number): Promise<void> {
    const img = this.hostingImages[index];
    if (!img || !confirm('Delete this image?')) return;

    try {
      if (img.uploaded && img.key) await this.s3UploadService.deleteImageFromS3(img.key);
      this.hostingImages.splice(index, 1);
    } catch (e) {
      console.error('Error deleting image:', e);
    }
  }

  async onHabitacionFilesSelected(event: any): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = input?.files;
  if (!files || files.length === 0) return;

  this.spaceImagesError = '';
  this.spaceImagesLoading = true;

  // NO reiniciar colecciones, solo mantenerlas
  this.spaceImages = this.spaceImages || [];
  this.spaceImagesPreview = this.spaceImagesPreview || [];

  try {
    for (const file of Array.from(files)) {
      const validation = this.s3UploadService.validateImage(file);
      if (!validation.valid) {
        this.spaceImagesError = validation.error || 'Archivo no válido';
        continue;
      }

      const preview = await this.s3UploadService.generatePreview(file);
      if (preview) this.spaceImagesPreview.push(preview);

      const uploadResp = await this.s3UploadService.uploadImageToS3(file, 'space');
      if (uploadResp?.success && uploadResp?.url) {
        this.spaceImages.push(uploadResp.url); // ✔ SE SUMAN
      } else {
        this.spaceImagesError = uploadResp?.error || 'Error subiendo imagen';
      }
    }
  } catch (err) {
    console.error('Error procesando imágenes:', err);
    this.spaceImagesError = 'Error procesando imágenes';
  } finally {
    this.spaceImagesLoading = false;
    input.value = '';
  }
}


  // ====================== ROOMS ======================
  addSpace(): void {
  const { number, type, price_per_night, capacity } = this.newSpace;
if (!number || !type || !price_per_night || !capacity) return;
  const space: Space = {
    id: `space-${Date.now()}`,
    number: Number(number),
    type,
    floor: Number(this.newSpace.floor) || 0,
    beds: Number(this.newSpace.beds) || 0,
    bathspaces: Number(this.newSpace.bathspaces) || 0,
    capacity: Number(this.newSpace.capacity), // 👈 AQUÍ
    price_per_night: Number(price_per_night),
    description: this.newSpace.description || '',
    images: [...this.spaceImages],
  };

  this.spaces.push(space);

  // ==== LIMPIEZA COMPLETA DEL CARGADOR ====
  this.newSpace = {};
  this.spaceImages = [];
  this.spaceImagesPreview = [];
  this.spaceImagesError = "";
  this.spaceImagesLoading = false;

  // limpiar input de archivo si existe
  const input = document.getElementById('spaceImagesInput') as HTMLInputElement;
  if (input) input.value = '';
}


  removeSpace(index: number): void {
    if (confirm('Delete this space?')) this.spaces.splice(index, 1);
  }

  // ====================== SERVICES ======================
  addExtraService(): void {
    if (!this.serviceExtraTemp.name || this.serviceExtraTemp.cost <= 0) return;
    this.extraServices.push({ ...this.serviceExtraTemp, id: `extra-${Date.now()}` });
    this.serviceExtraTemp = { id: '', name: '', cost: 0 };
  }

  removeExtraService(index: number): void {
    this.extraServices.splice(index, 1);
  }

  addIncludedService(): void {
    if (!this.serviceIncludedTemp) return;
    const serviceIncluded = this.avaibleServiceIncluded.find(service => service.name === this.serviceIncludedTemp);
    if (!serviceIncluded) return;
    this.includedServices.push(serviceIncluded);
  }

  removeIncludedService(index: number): void {
    this.includedServices.splice(index, 1);
  }

  // ====================== PAYMENTS ======================
  addPaymentMethod(method: string): void {
    if (method && !this.paymentMethods.includes(method)) {
      this.paymentMethods.push({ name: method });
    }
  }

  removePaymentMethod(index: number): void {
    this.paymentMethods.splice(index, 1);
  }

  // ====================== RULES ======================
  addRule(): void {
  if (!this.newRule.title || !this.newRule.description) return;
  const now = new Date().toISOString();

  this.rule = {
    id: `rule-${Date.now()}`, // ✅ Genera un ID único
    title: this.newRule.title,
    description: this.newRule.description,
    penalty: this.newRule.penalty || '',
    category: this.newRule.category || '',
  };

  this.newRule = {}; // Limpia el formulario
}

  removeRule(): void {
    this.rule = {
      id: '',
      title: '',
      category: '',
      description: '',
      penalty: ''
    };
  }


  // ====================== LOCATION ======================


  getLocaltionGPS(): void {
    if (!navigator.geolocation) {
      alert('Tu navegador no soporta geolocalización');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.onCoordinatesSelected({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        console.error('Error GPS:', error);
        alert('No se pudo obtener la ubicación GPS');
      }
    );
  }

  async onCoordinatesSelected(coords: { lat: number; lng: number }): Promise<void> {
    this.selectedCoords = coords;
    this.locationData.latitude = coords.lat.toString();
    this.locationData.longitude = coords.lng.toString();
    this.locationData.coordinates = `${coords.lat}, ${coords.lng}`;
    await this.reverseGeocode(coords.lat, coords.lng);
  }

  applyLatLng(): void {
    if (!this.locationData.latitude || !this.locationData.longitude) {
      alert('Completa latitud y longitud');
      return;
    }
    const lat = parseFloat(this.locationData.latitude);
    const lng = parseFloat(this.locationData.longitude);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert('Coordenadas inválidas');
      return;
    }
    this.selectedCoords = { lat, lng };
    this.locationData.coordinates = `${lat}, ${lng}`;
    this.reverseGeocode(lat, lng);
  }

  private async reverseGeocode(lat: number, lng: number): Promise<void> {
    try {
      const url = `${environment.openStreetMapUrl}&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=en`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data.address) throw new Error('No address found');

      const addr = data.address;
      this.locationData.full_address = this.buildFullAddress(data);
      this.locationData.city = addr.city || addr.town || '';
      this.locationData.country = addr.country || '';
      this.locationData.region = addr.state || '';
    } catch (err) {
      console.error('Reverse geocode error:', err);
    }
  }

  private buildFullAddress(data: any): string {
    const addr = data.address;
    const parts = [addr.road, addr.house_number, addr.city, addr.state]
      .filter(Boolean)
      .join(', ');
    return parts || 'Unknown address';
  }

  // ====================== SAVE & CLOSE ======================
  save(): void {
  if (!this.isFormValid()) {
    alert('Please complete all required fields');
    return;
  }

  this.isLoading = true;
  this.loadingMessage = 'Guardando alojamiento...';

  const hosting = this.buildHostingPayload();

  // Llamar al servicio para guardar el alojamiento
  this.hostingService.createHosting(hosting).subscribe({
    next: (response) => {
      this.isLoading = false;
      const successDialog = this.dialog.open(SuccessDialogComponent, {
        width: '350px',
        disableClose: true
      });
      successDialog.afterClosed().subscribe(result => {
        if (result === 'admin') {
          this.dialogRef.close({ action: 'goToAdmin', data: hosting });
          // Recargar la página después de cerrar el modal
          setTimeout(() => window.location.reload(), 300);
        }
        else if (result === 'new') {
          // Resetear formulario pero mantener modal abierto para crear otro
          this.resetForm();
        }
      });
    },
    error: (error) => {
      this.isLoading = false;
      console.error('Error al guardar alojamiento:', error);
      alert('Error al guardar el alojamiento. Por favor intenta de nuevo.');
    }
  });
}


  cancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  isFormValid(): boolean {
    return (
      this.validateBasicInfo() &&
      this.validateLocation() &&
      this.validateImages() &&
      this.validateSpaces() &&
      this.validatePaymentMethods()
    );
  }

  private buildHostingPayload(): any {
    const user = localStorage.getItem('user');
    const userId = user ? JSON.parse(user).id : '';
    return {
      id: this.basicInfo.id || this.generateHostingId(),
      name: this.basicInfo.name,
      email: this.basicInfo.email,
      phone: this.basicInfo.phone,
      type: this.basicInfo.type,
      description: this.basicInfo.description,
      capacity: this.basicInfo.capacity,
      location: this.locationData,
      images: this.uploadedImages.map(i => i.url),
      user_id: userId,
      spaces: this.spaces,
      extra_services: this.extraServices,
      included_services: this.includedServices,
      payment_methods: this.paymentMethods,
      rule: this.rule || null,
      updated_at: new Date().toISOString()
    };
  }

  private generateHostingId(): string {
    const nameSlug = this.basicInfo.name.trim().toLowerCase().replace(/\s+/g, '-');
    return `hosting-${nameSlug}-${Date.now()}`;
  }

  scrollModalToTop() {
  const container = document.querySelector('.cdk-global-scrollblock .mdc-dialog__content');
  if (container) {
    container.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
}

addAnotherSpace() {
  this.scrollModalToTop();
}

finishSpaces() {
  this.addSpace();
  this.nextStep();
}

resetForm(): void {
  this.currentStep = 0;

  // Reset Basic Info
  this.basicInfo = {
    status: true,
    name: '',
    email: '',
    phone: '',
    type: '',
    capacity: '',
    description: '',
    user_id: ''
  };

  // Reset Location
  this.locationData = {
    full_address: '',
    coordinates: '',
    latitude: '',
    longitude: '',
    city: '',
    country: '',
    region: ''
  };
  this.selectedCoords = { lat: 4.7110, lng: -74.0721 };

  // Reset Images
  this.hostingImages = [];
  this.uploadProgress = { visible: false, percentage: 0, uploaded: 0, total: 0 };

  // Reset Spaces
  this.spaces = [];
  this.newSpace = {};
  this.spaceImages = [];
  this.spaceImagesPreview = [];
  this.spaceImagesError = "";
  this.spaceImagesLoading = false;

  // Reset Services
  this.extraServices = [];
  this.includedServices = [];
  this.paymentMethods = [];

  // Reset Rules
  this.rule = {
    id: '',
    title: '',
    category: '',
    description: '',
    penalty: ''
  };
  this.newRule = {};

  // Subir al inicio visual del modal
  this.scrollModalToTop();
}

}
